import { LinearProgress, Stack } from '@mui/material';
import { ApolloError } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import {
  IPaginationProps,
  LoadedContent,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@occc-app/design-system';
import { MessagingCampaignsListItemFragment } from '@occc/graphql';

import { MessagingCampaignsListItem } from './messaging-campaigns-list-item.component';
import { MessagingCampaignsListItemContextMenu } from './messaging-campaigns-list-item-context-menu.component';
import { MessagingCampaignStatusesDescriptionTooltip } from './messaging-campaign-statuses-description-tooltip.component';

import { routeCreator } from '../../../messaging-center.utils';
import { MessagingCampaignTabs } from '../../messaging-campaign-configure/messaging-campaign-configure.constants';

export type MessagingCampaignsTableProps = {
  campaigns?: MessagingCampaignsListItemFragment[];
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
  isUserPermittedToEdit: boolean;
  isUserPermittedToArchive: boolean;
  onArchiveClick: (campaignUuid: string) => void;
};

export const MessagingCampaignsTable = ({
  campaigns,
  loading,
  error,
  paginationProps,
  isUserPermittedToEdit,
  isUserPermittedToArchive,
  onArchiveClick,
}: MessagingCampaignsTableProps) => {
  const navigate = useNavigate();

  const renderData = (items: MessagingCampaignsListItemFragment[]) => {
    const rows = items.map((campaign) => {
      const onViewDetailsHandler = (campaignUuid: string) => {
        navigate(routeCreator.campaignDetails(campaignUuid));
      };

      const onEditHandler = (campaignUuid: string) =>
        navigate(routeCreator.campaignUpdateWithTab(campaignUuid, MessagingCampaignTabs.Configure));

      return (
        <MessagingCampaignsListItem
          key={campaign.uuid}
          campaign={campaign}
          onClick={() => {
            onViewDetailsHandler(campaign.uuid);
          }}
          contextMenu={
            <MessagingCampaignsListItemContextMenu
              campaignUuid={campaign.uuid}
              status={campaign.status}
              isUserPermittedToEdit={isUserPermittedToEdit}
              isUserPermittedToArchive={isUserPermittedToArchive}
              onViewDetailsClick={onViewDetailsHandler}
              onEditClick={onEditHandler}
              onArchiveClick={onArchiveClick}
            />
          }
        />
      );
    });

    return (
      <>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Message Type</TableCell>
              <TableCell>Scheduled / Sent</TableCell>
              <TableCell>
                <Stack flexDirection='row' gap={2}>
                  Status
                  <MessagingCampaignStatusesDescriptionTooltip />
                </Stack>
              </TableCell>
              {/* table cell for three dots */}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
        <Pagination {...paginationProps} />
      </>
    );
  };

  return (
    <LoadedContent<MessagingCampaignsListItemFragment>
      error={error}
      loading={loading}
      items={campaigns}
      renderData={renderData}
    />
  );
};
